import React from 'react';
import gql from 'graphql-tag';
import { useQuery, useMutation, MutationFunction } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Layout, LayoutContent, Navigation } from '../../../components';
import { TopBarDropdown } from '../../../components/TopBarDropdown';

import { CopyMenu, CopyMenuVariables } from './__generated__/CopyMenu';
import { CreateMenu, CreateMenuVariables } from './__generated__/CreateMenu';
import { DeleteMenu, DeleteMenuVariables } from './__generated__/DeleteMenu';
import { DeleteMenuItem, DeleteMenuItemVariables } from './__generated__/DeleteMenuItem';
import { Menu, MenuVariables } from './__generated__/Menu';
import { UpdateMenu, UpdateMenuVariables } from './__generated__/UpdateMenu';
import { UpdateMenuItemStatus, UpdateMenuItemStatusVariables } from './__generated__/UpdateMenuItemStatus';
import { CopyMenuItem, CopyMenuItemVariables } from './__generated__/CopyMenuItem';
import { UpdateMenuCategory, UpdateMenuCategoryVariables } from './__generated__/UpdateMenuCategory';

import { AddModal } from './AddModal';
import { AddItemModal } from './AddItemModal';
import { EditModal } from './EditModal';
import { CopyModal } from './CopyModal';
import { DeleteModal } from './DeleteModal';
import { Duplicate } from './Duplicate';
import { AddIcon } from './AddIcon';
import { EditIcon } from './EditIcon';
import { TrashCan } from './TrashCan';
import { CopyMenuItemModal } from './CopyMenuItemModal';

import { createSalesCategoryListWithIndents } from './utils';
import { SalesCategory, BasicItem } from './types';

import { Menu_menu_categories_items as MenuItem } from './__generated__/Menu';
import { MenuList } from './MenuList';

import './Menu.scss';
import { MenuCategoryInput } from '../../../globalTypes';

interface MenuState {
  modal: false | 'add' | 'edit' | 'copy' | 'delete';
  addMenuItemModalOpen: boolean;
  itemToCopy?: MenuItem;
}

interface MenuRouterParams {
  venueId: string;
  menuId: string;
  categoryId?: string;
}

type MenuRouterProps = RouteComponentProps<MenuRouterParams>;

interface MenuProps extends Pick<MenuRouterProps, 'history'> {
  venueId: string;
  menuId: string;
  categoryId: string;
  updateMenuItem: MutationFunction<UpdateMenuItemStatus, UpdateMenuItemStatusVariables>;
  deleteMenuItem: MutationFunction<DeleteMenuItem, DeleteMenuItemVariables>;
  createMenu: MutationFunction<CreateMenu, CreateMenuVariables>;
  updateMenu: MutationFunction<UpdateMenu, UpdateMenuVariables>;
  deleteMenu: MutationFunction<DeleteMenu, DeleteMenuVariables>;
  copyMenu: MutationFunction<CopyMenu, CopyMenuVariables>;
  copyMenuItem: MutationFunction<CopyMenuItem, CopyMenuItemVariables>;
  updateMenuCategory: MutationFunction<UpdateMenuCategory, UpdateMenuCategoryVariables>;

  data?: Menu;
  saleCategories?: SalesCategory[];
  error?: Error;
  loading: boolean;
}

const CREATE_MENU = gql`
  mutation CreateMenu($venueId: ID!, $name: String!, $menuCategories: [MenuCategoryInput!]) {
    createMenu(venueId: $venueId, name: $name, menuCategories: $menuCategories)
  }
`;

const UPDATE_MENU = gql`
  mutation UpdateMenu($venueId: ID!, $menuId: ID!, $name: String, $menuCategories: [MenuCategoryInput!]) {
    updateMenu(venueId: $venueId, menuId: $menuId, name: $name, menuCategories: $menuCategories)
  }
`;

const UPDATE_MENU_CATEGORY = gql`
  mutation UpdateMenuCategory($menuId: ID!, $menuCategoryId: ID!, $itemIds: [ID!]!) {
    updateMenuCategory(menuId: $menuId, menuCategoryId: $menuCategoryId, itemIds: $itemIds)
  }
`;

const COPY_MENU = gql`
  mutation CopyMenu($venueId: ID!, $fromMenuId: ID!, $toMenuId: ID!) {
    copyMenu(venueId: $venueId, fromMenuId: $fromMenuId, toMenuId: $toMenuId)
  }
`;

const COPY_MENU_ITEM = gql`
  mutation CopyMenuItem($venueId: ID!, $fromMenuId: ID!, $toMenuId: ID!, $toMenuCategoryId: ID!, $itemId: ID!) {
    copyMenuItem(
      venueId: $venueId
      fromMenuId: $fromMenuId
      toMenuId: $toMenuId
      toMenuCategoryId: $toMenuCategoryId
      itemId: $itemId
    )
  }
`;

const DELETE_MENU = gql`
  mutation DeleteMenu($venueId: ID!, $menuId: ID!) {
    deleteMenu(venueId: $venueId, menuId: $menuId)
  }
`;

const UPDATE_MENU_ITEM = gql`
  mutation UpdateMenuItemStatus($menuId: ID!, $categoryId: ID!, $itemId: ID!, $menuItem: MenuItemInput!) {
    updateMenuItem(menuId: $menuId, categoryId: $categoryId, itemId: $itemId, menuItem: $menuItem) {
      item {
        id
      }
    }
  }
`;

const DELETE_MENU_ITEM = gql`
  mutation DeleteMenuItem($menuId: ID!, $categoryId: ID!, $itemId: ID!) {
    deleteMenuItem(menuId: $menuId, categoryId: $categoryId, itemId: $itemId)
  }
`;

const MENU_QUERY = gql`
  query Menu($venueId: ID!, $menuId: ID!) {
    menus(venueId: $venueId) {
      id
      name
    }
    salesCategories {
      id
      name
      parentId
    }
    menu(venueId: $venueId, menuId: $menuId) {
      id
      name
      posItemsOnly
      categories {
        id
        name
        salesCategory {
          id
          name
        }
        items {
          enabled
          featured
          price
          item {
            id
            name
            shared
            imageUrl
            menuCategoryId
            alcoholServings
            description
          }
        }
      }
    }
  }
`;

class MenuUnwrapped extends React.Component<MenuProps, MenuState> {
  constructor(props) {
    super(props);

    this.state = { modal: false, addMenuItemModalOpen: false };
  }

  deleteMenuItem = async (menuItem: MenuItem) => {
    await this.props.deleteMenuItem({
      variables: {
        itemId: menuItem.item.id,
        categoryId: this.props.categoryId,
        menuId: this.props.menuId,
      },
    });
  };

  onToggleFeatured = async (menuItem: MenuItem) => {
    let id: any = this.props.categoryId == '-1' ? menuItem.item.menuCategoryId : this.props.categoryId;
    await this.props.updateMenuItem({
      variables: {
        categoryId: id,
        itemId: menuItem.item.id,
        menuId: this.props.menuId,
        menuItem: {
          featured: !menuItem.featured,
          price: menuItem.price,
        },
      },
    });
  };

  onToggleEnabled = async (menuItem: MenuItem) => {
    await this.props.updateMenuItem({
      variables: {
        categoryId: this.props.categoryId,
        itemId: menuItem.item.id,
        menuId: this.props.menuId,
        menuItem: {
          enabled: !menuItem.enabled,
          price: menuItem.price,
        },
      },
    });
  };

  closeModal = () => this.setState({ modal: false });

  create = ({ name, menuCategories }: { name: string; menuCategories: MenuCategoryInput[] }) => {
    this.setState({ modal: false }, async () => {
      await this.props.createMenu({
        variables: {
          venueId: this.props.venueId,
          name,
          menuCategories,
        },
      });
    });
  };

  update = async (itemIds: string[]) => {
    if (Number(this.props.categoryId) > 0) {
      await this.props.updateMenuCategory({
        variables: {
          menuId: this.props.menuId,
          menuCategoryId: this.props.categoryId,
          itemIds,
        },
      });
    }
  };

  updateMenu = ({ name, menuCategories }: { name: string; menuCategories: MenuCategoryInput[] }) => {
    this.setState({ modal: false }, async () => {
      await this.props.updateMenu({
        variables: {
          venueId: this.props.venueId,
          menuId: this.props.menuId,
          name,
          menuCategories,
        },
      });
    });
  };

  delete = () => {
    this.setState({ modal: false }, async () => {
      await this.props.deleteMenu({
        variables: {
          venueId: this.props.venueId,
          menuId: this.props.menuId,
        },
      });
    });
  };

  copy = (fromMenuId, toMenuId) => {
    this.setState({ modal: false }, async () => {
      await this.props.copyMenu({
        variables: {
          venueId: this.props.venueId,
          fromMenuId,
          toMenuId,
        },
      });

      this.props.history.push(`/venue/${this.props.venueId}/menu/list/${toMenuId}`);
    });
  };

  openCreateModal = () => this.setState({ modal: 'add' });
  openClickEdit = () => this.setState({ modal: 'edit' });
  openCopyModal = () => this.setState({ modal: 'copy' });
  openDeleteModal = () => this.setState({ modal: 'delete' });

  copyMenuItem = (itemToCopy: MenuItem) => {
    this.setState({ itemToCopy });
  };

  toggleCopyItem = () => {
    this.setState({ itemToCopy: undefined });
  };

  submitCopyMenuItem = ({ toMenuId, toMenuCategoryId }: { toMenuId: string; toMenuCategoryId: string }) => {
    const itemId = this.state.itemToCopy.item.id;
    this.setState({ itemToCopy: undefined }, async () => {
      await this.props.copyMenuItem({
        variables: {
          venueId: this.props.venueId,
          fromMenuId: this.props.menuId,
          toMenuCategoryId,
          toMenuId,
          itemId,
        },
      });

      this.props.history.push(`/venue/${this.props.venueId}/menu/list/${toMenuId}`);
    });
  };

  goToMenu = ({ id }) => {
    const { venueId } = this.props;
    this.props.history.push(`/venue/${venueId}/menu/list/${id}`);
  };

  handleClickAddMenuItem = () => {
    this.setState({
      addMenuItemModalOpen: true,
    });
  };

  handleClickCancelAddMenuItem = () => {
    this.setState({ addMenuItemModalOpen: false });
  };

  handleAddItem = (item: BasicItem | null) => {
    if (item) {
      this.setState(
        {
          addMenuItemModalOpen: false,
        },
        async () => {
          const { categoryId } = this.props;
          const { data } = await this.props.updateMenuItem({
            variables: {
              menuId: this.props.menuId,
              categoryId,
              itemId: `${item.id}`,
              menuItem: {
                price: 0,
                enabled: true,
              },
            },
          });

          const { updateMenuItem } = data;

          const { venueId, menuId } = this.props;
          const id = updateMenuItem.item.id;

          this.props.history.push(`/venue/${venueId}/menu/${menuId}/category/${categoryId}/item/${id}`);
        }
      );
    } else {
      const { menuId, venueId, categoryId } = this.props;

      this.props.history.push(`/venue/${venueId}/menu/${menuId}/category/${categoryId}/add`);
    }
  };

  render() {
    const leftContent = this.props.data && (
      <TopBarDropdown
        leftBorder={true}
        label={this.props.data.menu.name}
        options={this.props.data.menus}
        onSelect={this.goToMenu}
        className='d-none d-md-flex'
      />
    );

    const { venueId } = this.props;

    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>My Menu</Layout.TopBar.Title>
            {leftContent}
          </Layout.TopBar.Left>
          <Layout.TopBar.Right>
            {this.props.data && (
              <div className='menu-controls'>
                <AddIcon onClick={this.openCreateModal} />
                <EditIcon onClick={this.openClickEdit} />
                <Duplicate onClick={this.openCopyModal} disabled={false} />
                <TrashCan onClick={this.openDeleteModal} disabled={this.props.data.menus.length === 1} />
              </div>
            )}
          </Layout.TopBar.Right>
        </Layout.TopBar>
        <LayoutContent error={this.props.error} className='menu-container'>
          <MenuList
            loading={this.props.loading}
            categories={this.props.data?.menu?.categories ?? []}
            onToggleFeatured={this.onToggleFeatured}
            onOrderChange={this.update}
            onDeleteItem={this.deleteMenuItem}
            onToggleEnabled={this.onToggleEnabled}
            venueId={venueId}
            menuId={this.props.menuId}
            categoryId={this.props.categoryId}
            onClickAdd={this.handleClickAddMenuItem}
            name={this.props.data?.menu.name}
            history={this.props.history}
            copyMenuItem={this.copyMenuItem}
            addCategories={this.openClickEdit}
          />
          {this.props.data && (
            <AddModal
              saleCategories={this.props.saleCategories}
              toggle={this.closeModal}
              open={this.state.modal === 'add'}
              onSubmit={this.create}
            />
          )}
          {this.state.modal === 'edit' && (
            <EditModal
              saleCategories={this.props.saleCategories}
              menu={this.props.data?.menu}
              toggle={this.closeModal}
              onSubmit={this.updateMenu}
            />
          )}
          <DeleteModal toggle={this.closeModal} open={this.state.modal === 'delete'} onDelete={this.delete} />
          {this.props.data && (
            <CopyModal
              menus={this.props.data.menus}
              toggle={this.closeModal}
              open={this.state.modal === 'copy'}
              onSubmit={this.copy}
            />
          )}
          {this.state.itemToCopy && this.props.data && (
            <CopyMenuItemModal
              toggle={this.toggleCopyItem}
              menus={this.props.data.menus}
              venueId={this.props.venueId}
              copyMenuItem={this.submitCopyMenuItem}
              menuItem={this.state.itemToCopy}
              currentMenu={this.props.data.menu}
            />
          )}
          {this.state.addMenuItemModalOpen && (
            <AddItemModal
              allowAddingNew={!this.props.data?.menu.posItemsOnly}
              onCancel={this.handleClickCancelAddMenuItem}
              onSave={this.handleAddItem}
              venueId={this.props.venueId}
              menuId={this.props.menuId}
              menuCategoryId={this.props.categoryId}
            />
          )}
        </LayoutContent>
      </Layout>
    );
  }
}

export const MenuContainer: React.FC<MenuRouterProps> = (props) => {
  const { match } = props;
  const { venueId, menuId, categoryId } = match.params;
  const menuQueryState = useQuery<Menu, MenuVariables>(MENU_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      venueId,
      menuId,
    },
  });

  const hierarchialSalesCategories = React.useMemo(() => {
    if (menuQueryState.data) {
      return createSalesCategoryListWithIndents(menuQueryState.data.salesCategories);
    }
  }, [menuQueryState]);

  const refetch = React.useCallback(() => {
    menuQueryState.refetch();
  }, [menuQueryState]);

  const [createMenu] = useMutation<CreateMenu, CreateMenuVariables>(CREATE_MENU, {
    onCompleted(data) {
      props.history.push(`/venue/${venueId}/menu/list/${data.createMenu}`);
    },
  });

  const [updateMenuItem, updateMenuItemState] = useMutation<UpdateMenuItemStatus, UpdateMenuItemStatusVariables>(
    UPDATE_MENU_ITEM,
    {
      onCompleted: refetch,
    }
  );
  const [deleteMenuItem, deleteMenuItemState] = useMutation<DeleteMenuItem, DeleteMenuItemVariables>(DELETE_MENU_ITEM, {
    onCompleted: refetch,
  });

  const [updateMenu, updateMenuState] = useMutation<UpdateMenu, UpdateMenuVariables>(UPDATE_MENU, {
    onCompleted: refetch,
  });

  const [updateMenuCategory] = useMutation<UpdateMenuCategory, UpdateMenuCategoryVariables>(UPDATE_MENU_CATEGORY, {});

  const [deleteMenu, deleteMenuState] = useMutation<DeleteMenu, DeleteMenuVariables>(DELETE_MENU, {
    onCompleted: refetch,
  });

  const [copyMenu, copyMenuState] = useMutation<CopyMenu, CopyMenuVariables>(COPY_MENU, {
    onCompleted: refetch,
  });

  const [copyMenuItem, copyMenuItemState] = useMutation<CopyMenuItem, CopyMenuItemVariables>(COPY_MENU_ITEM, {
    onCompleted: refetch,
  });

  return (
    <MenuUnwrapped
      venueId={venueId}
      menuId={menuId}
      categoryId={categoryId ?? '-1'}
      createMenu={createMenu}
      updateMenuItem={updateMenuItem}
      deleteMenuItem={deleteMenuItem}
      updateMenu={updateMenu}
      deleteMenu={deleteMenu}
      copyMenu={copyMenu}
      copyMenuItem={copyMenuItem}
      updateMenuCategory={updateMenuCategory}
      saleCategories={hierarchialSalesCategories}
      history={props.history}
      loading={menuQueryState.loading || updateMenuItemState.loading}
      data={menuQueryState.data}
      error={
        menuQueryState.error ??
        updateMenuItemState.error ??
        deleteMenuItemState.error ??
        updateMenuState.error ??
        copyMenuState.error ??
        copyMenuItemState.error ??
        deleteMenuState.error
      }
    />
  );
};
